import type { MilestoneDetail } from "@kundeninfo/api-client";
import { formatDatum } from "../../format.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";

/**
 * TEXTE DER MEILENSTEINE (Spec D21, „Texte und Symbole“; Plan Aufgabe 8) — EINE Tabelle für das Panel, den Abschnitt
 * „Meilensteine“ im Reiter „Verknüpfungen“, die Zeile im Abschnitt „Termine“ und die zwei Ereignisse im Ticketverlauf.
 * Schlüssel englisch, Werte deutsch. „+ Meilenstein“ steht einmal in `DUE_ITEM_TEXT` (Zeitachse, Liste, Kalender).
 */

type State = MilestoneDetail["state"];
type TicketState = MilestoneDetail["tickets"][number]["stateKind"];
type Event = MilestoneDetail["events"][number];

const STATES: Readonly<Record<State, string>> = { OPEN: "offen", REACHED: "erreicht", CANCELLED: "abgesagt" };
/** Wie die Zeitachse (`TIMELINE_TEXT.states`, Review H5). */
const TICKET_STATES: Readonly<Record<TicketState, string>> = { OPEN: "offen", WAITING: "wartend", DONE: "erledigt" };

const SYSTEM = "System";
const text = (w: unknown): string => (typeof w === "string" ? w : "");
const day = (w: unknown): string => (typeof w === "string" && w !== "" ? formatDatum(w) : "–");
const state = (w: unknown): string => STATES[w as State] ?? text(w);
/** Ein Ticket-Ereignis nennt die Nummer nur, wenn der Anfragende das Ticket sieht (Aufgabe 7, G1: sonst `detail: {}`). */
const number = (d: Record<string, unknown>): string | null => (typeof d.displayNumber === "string" ? d.displayNumber : null);

/** Verlaufstexte je Art (`milestoneEvent`, D6); Unbekanntes zeigt die Art selbst, nie „undefined“. */
const EVENT_TEXT: Readonly<Record<Event["eventType"], (d: Record<string, unknown>) => string>> = {
  CREATED: () => "angelegt",
  TITLE_CHANGED: (d) => `Titel: „${text(d.from)}“ → „${text(d.to)}“`,
  DUE_DATE_CHANGED: (d) => `Stichtag: ${day(d.from)} → ${day(d.to)}`,
  STATE_CHANGED: (d) => `Zustand: ${state(d.from)} → ${state(d.to)}`,
  DESCRIPTION_CHANGED: () => "Beschreibung geändert",
  TICKET_ADDED: (d) => { const nr = number(d); return nr === null ? "ein Ticket verknüpft" : `Ticket ${nr} verknüpft`; },
  TICKET_REMOVED: (d) => { const nr = number(d); return nr === null ? "Verknüpfung gelöst" : `Ticket ${nr} gelöst`; },
};

export const MILESTONE_TEXT = {
  title: "Meilenstein",
  titleNew: "Neuer Meilenstein",
  create: DUE_ITEM_TEXT.newMilestone,
  fields: { company: "Unternehmen", title: "Titel", dueDate: "Stichtag", state: "Zustand", description: "Beschreibung" },
  states: STATES,
  ticketStates: TICKET_STATES,
  tickets: "Tickets",
  /** Fortschritt „3/5 erledigt“ — zählt auch Tickets, die der Anfragende nicht sieht (T21). */
  progress: (done: number, total: number): string => `${done}/${total} erledigt`,
  noTickets: "Keine Tickets verknüpft.",
  nobody: "–",
  linkTicket: "Ticket verknüpfen …",
  linkTicketTitle: "Ticket verknüpfen",
  ticketSearch: "Ticket suchen",
  ticketSearchHint: "Nummer oder Titel",
  hits: "Treffer",
  noHits: "Keine Treffer.",
  hit: (nr: string, title: string): string => `${nr} · ${title}`,
  link: "Verknüpfen",
  unlink: "Lösen",
  unlinkName: (nr: string): string => `${nr} lösen`,
  unlinkQuestion: (nr: string): string => `${nr} von diesem Meilenstein lösen?`,
  unlinkDetail: "Das Ticket bleibt, wie es ist; nur die Verknüpfung wird gelöst.",
  allDone: "Alle Tickets erledigt – Meilenstein als erreicht markieren?",
  markReached: "Als erreicht markieren",
  history: "Verlauf",
  noHistory: "Noch kein Verlauf.",
  /** Eine Verlaufszeile: „12.10.2026 11:00 · Anna: Titel: „A“ → „B““ — Zeit kommt vom Aufrufer (Berliner Zeitpunkt). */
  eventLine: (when: string, e: Event): string =>
    `${when} · ${e.user?.name ?? SYSTEM}: ${EVENT_TEXT[e.eventType]?.(e.detail) ?? e.eventType}`,
  save: "Speichern",
  loadError: "Der Meilenstein konnte nicht geladen werden.",
  saveError: "Der Meilenstein ließ sich nicht speichern.",
  conflict: "Der Meilenstein wurde inzwischen geändert – der aktuelle Stand ist geladen, die Eingaben sind verworfen.",
  conflictReloadFailed: "Der Meilenstein wurde inzwischen geändert und ließ sich nicht neu laden.",
  linkError: "Die Verknüpfung ließ sich nicht anlegen.",
  unlinkError: "Die Verknüpfung ließ sich nicht lösen.",
  searchError: "Die Suche ist gerade nicht möglich.",
  forbidden: "Dafür fehlt die Berechtigung.",
  // Reiter „Verknüpfungen“ im Ticketdialog
  section: "Meilensteine",
  sectionEmpty: "Keine Meilensteine verknüpft.",
  sectionLoadError: "Die Meilensteine konnten nicht geladen werden.",
  linkMilestone: "Mit Meilenstein verknüpfen …",
  linkMilestoneTitle: "Mit Meilenstein verknüpfen",
  milestoneSearch: "Meilenstein suchen",
  milestoneSearchHint: "Titel",
  noOpenMilestones: "Keine offenen Meilensteine.",
  /** Treffer der Suche: „Rollout Portal · Di 15.12.2026“. */
  milestoneHit: (title: string, dueDay: string): string => `${title} · ${dueDay}`,
  // Ticketverlauf (Ereignisse am Ticket, Detail `{ milestoneId, title }`; ohne Titel ohne Namen, Review H2)
  ticketEventAdded: (title: string | null): string => (title === null ? "ein Meilenstein verknüpft" : `Meilenstein ${title} verknüpft`),
  ticketEventRemoved: (title: string | null): string => (title === null ? "ein Meilenstein gelöst" : `Meilenstein ${title} gelöst`),
} as const;
